import type { Job } from '@/domains/jobs/components/JobCard';
import { Clock, MapPin } from 'lucide-react';
import { useDrag } from 'react-dnd';

interface GanttJobBarProps {
  job: Job;
  left: number; // Pixels from left edge
  width: number; // Pixels wide
  onJobClick?: (jobId: string) => void;
}

export function GanttJobBar({ job, left, width, onJobClick }: GanttJobBarProps) {
  const [{ isDragging }, drag] = useDrag({
    type: 'JOB',
    item: { ...job, isGanttDrag: true },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  // Status colors
  const statusColors = {
    pending: 'bg-slate-400 border-slate-500',
    'in-progress': 'bg-orange-500 border-orange-600',
    completed: 'bg-green-500 border-green-600',
    urgent: 'bg-red-500 border-red-600',
  };

  // Priority indicator (left border)
  const priorityBorders = {
    high: 'border-l-4 border-l-red-600',
    medium: 'border-l-4 border-l-yellow-600',
    low: 'border-l-4 border-l-blue-600',
  };

  const statusColor = statusColors[job.status] || statusColors.pending;
  const priorityBorder = priorityBorders[job.priority] || '';

  return (
    <div
      ref={drag}
      className={`absolute top-1 h-12 rounded cursor-move shadow-sm hover:shadow-md transition-all ${statusColor} ${priorityBorder} ${
        isDragging ? 'opacity-50' : 'opacity-100'
      }`}
      style={{
        left: `${left}px`,
        width: `${Math.max(width, 60)}px`, // Minimum 60px width
        zIndex: isDragging ? 50 : 10,
      }}
      onClick={() => onJobClick?.(job.id)}
      title={`${job.title}${job.location ? ` • ${job.location}` : ''}${job.duration ? ` • ${job.duration}h` : ''} (Click to view details, drag to reschedule)`}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onJobClick?.(job.id);
        }
      }}
    >
      <div className="px-2 py-1 h-full flex flex-col justify-center text-white text-xs overflow-hidden">
        <div className="font-semibold truncate">{job.title}</div>
        <div className="flex items-center gap-2 mt-0.5 text-white/90">
          {job.duration && (
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              <span>{job.duration}h</span>
            </div>
          )}
          {job.location && width > 150 && (
            <div className="flex items-center gap-1 flex-1 min-w-0">
              <MapPin className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">{job.location}</span>
            </div>
          )}
        </div>
      </div>

      {/* Resize handles (for future Phase 6) */}
      {/* <div className="absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize hover:bg-white/20" /> */}
    </div>
  );
}
